/**
 * O chat do Assistente: lista de mensagens + composer com anexos e microfone.
 * Usado em tela cheia (/assistente) e no painel lateral (dock) — a diferença
 * é só o `compacto`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Streamdown } from "streamdown";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Brain,
  Check,
  Copy,
  Eye,
  ExternalLink,
  FileSignature,
  FileText,
  Headset,
  Image as ImageIcon,
  Inbox,
  Instagram,
  Loader2,
  Mail,
  Megaphone,
  MessageCircle,
  Mic,
  Music,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Settings,
  Sparkles,
  Square,
  Target,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { toUserFriendlyError } from "@/lib/error-messages";
import { ASSISTENTE_LIMITES, ASSISTENTE_MIMES_ACEITOS, type AssistenteAcao, type AssistentePendencia } from "@shared/assistente-contracts";
import { caminhoPermitido } from "@shared/assistente-telas";
import { type AnexoUi, type EtapaUi, type FerramentaUi, type MensagemUi, type SessaoAssistente } from "./useAssistente";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { usePermissions } from "@/hooks/usePermissions";
import { ASSISTENTE_MODULOS, SUGESTOES_GERAIS, type ModuloAssistente } from "@shared/assistente-modulos";
import { respostaDePropaganda } from "./propaganda";
import { BotaoUpgrade, CartaoUpgrade } from "./AssistenteUpgrade";

/** Ícone do módulo: o catálogo é compartilhado (sem React), então o nome vira componente aqui. */
const ICONES: Record<string, LucideIcon> = {
  MessageCircle, Inbox, Mail, Instagram, Megaphone, Target, Users, Brain, FileSignature, Headset, Settings, BookOpen,
};

function IconeModulo({ nome, className }: { nome: string; className?: string }) {
  const Ico = ICONES[nome] ?? Sparkles;
  return <Ico className={className} />;
}

/**
 * A sessão (`useAssistente`) vem de FORA: quem hospeda decide onde ela vive.
 * No dock, o hook mora no componente do botão flutuante — fechar o painel
 * desmonta este chat, mas o turno continua e reaparece ao reabrir.
 */
export function AssistenteChat(props: {
  sessao: SessaoAssistente;
  onNavegou?: () => void;
  compacto?: boolean;
  className?: string;
}) {
  const { sessao, onNavegou, compacto, className } = props;
  const status = trpc.assistente.status.useQuery(undefined, { staleTime: 60_000 });
  const { mensagens, ocupado, etapa, enviar: enviarAoServidor, responderLocalmente, refazerDe, responderConfirmacao, cancelar, carregando, modulo, setModulo } = sessao;
  const [, setLocation] = useLocation();
  const { can } = usePermissions();
  const modulosDisponiveis = useMemo(() => ASSISTENTE_MODULOS.filter((m) => !m.permissao || can(m.permissao)), [can]);
  // Só vale a área que este perfil alcança: sem isso, um módulo lembrado de
  // quando a pessoa tinha mais acesso seguiria mostrando chip e sugestões que
  // o servidor recusa.
  const moduloAtivo = modulosDisponiveis.find((m) => m.id === modulo);
  const fimRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: ocupado ? "auto" : "smooth", block: "end" });
  }, [mensagens, ocupado]);

  // Plano sem o Assistente não fecha o campo: a pessoa escreve, e a resposta
  // mostra o que ela teria — usando o próprio pedido como demonstração. Já a
  // IA não configurada é um problema de setup, não de venda: aí o aviso seco
  // é o certo, porque existe alguém no workspace que resolve.
  const semPlano = Boolean(status.data && !status.data.habilitadoNoPlano);
  const bloqueio = status.data && status.data.habilitadoNoPlano && !status.data.iaConfigurada
    ? "A IA não está configurada neste workspace."
    : null;

  const enviar = useCallback(
    (texto: string, anexos: AnexoUi[] = []) => {
      if (semPlano) {
        responderLocalmente(texto, respostaDePropaganda(texto));
        return;
      }
      enviarAoServidor(texto, anexos);
    },
    [enviarAoServidor, responderLocalmente, semPlano],
  );

  const clicarAcao = useCallback(
    (a: AssistenteAcao) => {
      if (a.tipo === "abrir") {
        if (caminhoPermitido(a.path)) {
          setLocation(a.path);
          onNavegou?.();
        }
      } else if (a.tipo === "enviar") {
        enviar(a.texto);
      } else if (a.tipo === "link") {
        window.open(a.url, "_blank", "noopener,noreferrer");
      }
    },
    [enviar, onNavegou, setLocation],
  );

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div className={cn("flex-1 min-h-0 overflow-y-auto", compacto ? "px-3 py-3" : "px-4 py-6")}>
        {carregando ? (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : mensagens.length === 0 ? (
          <VazioComSugestoes compacto={compacto} onEscolher={(t) => enviar(t)} bloqueio={bloqueio} semPlano={semPlano} modulo={moduloAtivo} />
        ) : (
          <div className={cn("mx-auto flex flex-col gap-4", compacto ? "max-w-full" : "max-w-3xl")}>
            {mensagens.map((m, i) => (
              <Mensagem
                key={m.id}
                m={m}
                compacto={compacto}
                onAcao={clicarAcao}
                onConfirmar={responderConfirmacao}
                ocupado={ocupado}
                ehUltima={i >= mensagens.length - 2}
                etapa={i === mensagens.length - 1 ? etapa : null}
                onRefazer={refazerDe}
                perguntaAnterior={typeof mensagens[i - 1]?.id === "number" && mensagens[i - 1]?.role === "user" ? (mensagens[i - 1].id as number) : undefined}
              />
            ))}
            <div ref={fimRef} />
          </div>
        )}
      </div>
      <Composer
        compacto={compacto}
        ocupado={ocupado}
        desabilitado={!!bloqueio || status.isLoading}
        microfone={!semPlano && (status.data?.microfone ?? false)}
        permiteAnexo={!semPlano}
        onEnviar={enviar}
        onCancelar={cancelar}
        modulos={modulosDisponiveis}
        moduloAtivo={moduloAtivo}
        onEscolherModulo={setModulo}
      />
    </div>
  );
}

// ─── Mensagens ───────────────────────────────────────────────────────────────

function Mensagem(props: {
  m: MensagemUi;
  compacto?: boolean;
  ocupado: boolean;
  ehUltima?: boolean;
  /** Só na última mensagem: o que o assistente está fazendo agora. */
  etapa?: EtapaUi;
  /** Id da pergunta que gerou esta resposta — habilita "refazer". */
  perguntaAnterior?: number;
  onAcao: (a: AssistenteAcao) => void;
  onConfirmar: (messageId: number, toolCallId: string, aprovado: boolean) => void;
  onRefazer: (messageId: number, textoNovo?: string) => void;
}) {
  const { m, compacto, ocupado, ehUltima, etapa, perguntaAnterior, onAcao, onConfirmar, onRefazer } = props;
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(m.content);
  const podeMexer = typeof m.id === "number" && !ocupado;

  if (m.role === "user") {
    if (editando) {
      return (
        <div className="flex justify-end">
          <div className={cn("w-full rounded-2xl border bg-card p-2", compacto ? "max-w-[95%]" : "max-w-[80%]")}>
            <Textarea
              value={rascunho}
              autoFocus
              rows={2}
              onChange={(e) => setRascunho(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditando(false);
                if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  if (rascunho.trim()) {
                    setEditando(false);
                    onRefazer(m.id as number, rascunho);
                  }
                }
              }}
              className="resize-none border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
            />
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => { setEditando(false); setRascunho(m.content); }}>
                Cancelar
              </Button>
              <Button size="sm" disabled={!rascunho.trim()} onClick={() => { setEditando(false); onRefazer(m.id as number, rascunho); }}>
                Enviar de novo
              </Button>
            </div>
            <p className="px-1 pb-1 text-[11px] text-muted-foreground">Editar refaz a conversa a partir daqui.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="group flex flex-col items-end gap-1">
        <div className={cn("rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-primary-foreground", compacto ? "max-w-[90%]" : "max-w-[80%]")}>
          {m.content && <p className="whitespace-pre-wrap text-sm">{m.content}</p>}
          {m.anexos && m.anexos.length > 0 && (
            <div className={cn("flex flex-wrap gap-1.5", m.content && "mt-2")}>
              {m.anexos.map((a) => (
                <ChipAnexo key={a.id} a={a} claro />
              ))}
            </div>
          )}
        </div>
        {podeMexer && (
          <div className="flex gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <BotaoAcaoMensagem titulo="Copiar" onClick={() => copiar(m.content)}>
              <Copy className="h-3.5 w-3.5" />
            </BotaoAcaoMensagem>
            {m.content && (
              <BotaoAcaoMensagem titulo="Editar e enviar de novo" onClick={() => { setRascunho(m.content); setEditando(true); }}>
                <Pencil className="h-3.5 w-3.5" />
              </BotaoAcaoMensagem>
            )}
            <BotaoAcaoMensagem titulo="Enviar de novo" onClick={() => onRefazer(m.id as number)}>
              <RefreshCw className="h-3.5 w-3.5" />
            </BotaoAcaoMensagem>
          </div>
        )}
      </div>
    );
  }

  const vazio = !m.content && m.ferramentas.length === 0 && !m.pendencia;
  return (
    <div className="group flex gap-3">
      <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color:var(--brand-accent)]/15 text-[color:var(--brand-accent)]">
        <Sparkles className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        {m.ferramentas.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {m.ferramentas.map((f) => (
              <ChipFerramenta key={f.id} f={f} />
            ))}
          </div>
        )}
        {m.content ? (
          <div className="prose prose-sm dark:prose-invert max-w-none [&_p]:my-1.5 [&_ul]:my-1.5 [&_ol]:my-1.5 [&_table]:text-xs">
            <Streamdown>{m.content}</Streamdown>
          </div>
        ) : null}
        {m.streaming && etapa && <LinhaDeEtapa etapa={etapa} />}
        {m.streaming && !etapa && vazio && (
          <p className="text-sm text-muted-foreground">
            <Loader2 className="mr-1.5 inline h-3.5 w-3.5 animate-spin" />
            Pensando…
          </p>
        )}
        {m.pendencia && (
          <CartaoConfirmacao
            pendencia={m.pendencia}
            desabilitado={ocupado || typeof m.id !== "number"}
            onRevisar={onAcao}
            onResposta={(ok) => typeof m.id === "number" && onConfirmar(m.id, m.pendencia!.toolCallId, ok)}
          />
        )}
        {m.upsell && <BotaoUpgrade className="mt-1" />}
        {m.acoes && m.acoes.length > 0 && !m.streaming && <Opcoes acoes={m.acoes} ocupado={ocupado} onAcao={onAcao} />}
        {!m.streaming && m.content && (
          <div className={cn("flex gap-0.5 transition-opacity", ehUltima ? "opacity-70" : "opacity-0 focus-within:opacity-100 group-hover:opacity-100")}>
            <BotaoAcaoMensagem titulo="Copiar resposta" onClick={() => copiar(m.content)}>
              <Copy className="h-3.5 w-3.5" />
            </BotaoAcaoMensagem>
            {perguntaAnterior !== undefined && !ocupado && (
              <BotaoAcaoMensagem titulo="Refazer esta resposta" onClick={() => onRefazer(perguntaAnterior)}>
                <RefreshCw className="h-3.5 w-3.5" />
              </BotaoAcaoMensagem>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BotaoAcaoMensagem({ titulo, onClick, children }: { titulo: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {children}
    </button>
  );
}

/** Argumento cru vira texto de gente: marcação some, JSON não vira parede. */
function legivel(v: unknown): string {
  if (typeof v !== "string") return JSON.stringify(v);
  const pareceHtml = /<\/?[a-z][\s\S]*>/i.test(v);
  if (!pareceHtml) return v;
  return v
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|tr|h[1-6]|li)>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Corta o que é longo e deixa abrir. O cartão existe para a pessoa DECIDIR;
 * um documento inteiro dentro dele afoga a decisão (e os botões).
 */
const LIMITE_VISIVEL = 320;

function ValorLongo({ texto }: { texto: string }) {
  const [aberto, setAberto] = useState(false);
  if (texto.length <= LIMITE_VISIVEL) return <span className="whitespace-pre-wrap break-words">{texto}</span>;
  return (
    <>
      <span className={cn("block whitespace-pre-wrap break-words", !aberto && "max-h-32 overflow-hidden")}>
        {aberto ? texto : `${texto.slice(0, LIMITE_VISIVEL)}…`}
      </span>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="mt-0.5 text-[11px] font-medium text-[color:var(--brand-accent)] hover:underline"
      >
        {aberto ? "Mostrar menos" : `Mostrar tudo (${texto.length.toLocaleString("pt-BR")} caracteres)`}
      </button>
    </>
  );
}

async function copiar(texto: string) {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success("Copiado");
  } catch {
    toast.error("Seu navegador bloqueou a cópia.");
  }
}

/**
 * "Consultando o funil… 4s". O contador é o que diferencia "está trabalhando"
 * de "travou" — sem ele, uma espera de 15s parece a tela ter morrido.
 */
function LinhaDeEtapa({ etapa }: { etapa: NonNullable<EtapaUi> }) {
  const [segundos, setSegundos] = useState(0);
  useEffect(() => {
    setSegundos(0);
    const t = setInterval(() => setSegundos(Math.round((Date.now() - etapa.desde) / 1000)), 1000);
    return () => clearInterval(t);
  }, [etapa.desde, etapa.texto]);
  return (
    <p className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-live="polite">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      <span>{etapa.texto}…</span>
      {segundos >= 3 && <span className="tabular-nums text-xs opacity-60">{segundos}s</span>}
    </p>
  );
}

/**
 * Duas ou mais opções de "seguir daqui" viram lista numerada e clicável, não
 * um punhado de pílulas: é uma pergunta esperando decisão, e lista lida de
 * cima para baixo deixa comparar. Opção única, atalho de tela e link seguem
 * como pílula.
 */
function Opcoes({ acoes, ocupado, onAcao }: { acoes: AssistenteAcao[]; ocupado: boolean; onAcao: (a: AssistenteAcao) => void }) {
  const escolhas = acoes.filter((a) => a.tipo === "enviar");
  const atalhos = acoes.filter((a) => a.tipo !== "enviar");
  return (
    <div className="space-y-2 pt-1">
      {escolhas.length >= 2 ? (
        <ul className="divide-y overflow-hidden rounded-xl border">
          {escolhas.map((a, i) => (
            <li key={i}>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => onAcao(a)}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50"
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] text-muted-foreground">{i + 1}</span>
                <span className="min-w-0 flex-1">{a.rotulo}</span>
                <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        escolhas.map((a, i) => (
          <Button key={i} size="sm" className="mr-2 h-8 rounded-full" disabled={ocupado} onClick={() => onAcao(a)}>
            {a.rotulo}
          </Button>
        ))
      )}
      {atalhos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {atalhos.map((a, i) => (
            <Button key={i} size="sm" variant="outline" className="h-8 rounded-full" onClick={() => onAcao(a)}>
              {a.tipo === "link" && <ExternalLink className="mr-1.5 h-3.5 w-3.5" />}
              {a.rotulo}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChipFerramenta({ f }: { f: FerramentaUi }) {
  const rotulo = f.titulo || f.nome.replace(/_/g, " ");
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]",
        f.emAndamento ? "border-primary/30 bg-primary/5 text-primary" : f.ok === false ? "border-destructive/30 bg-destructive/5 text-destructive" : "border-border bg-muted/40 text-muted-foreground",
      )}
      title={f.resumo}
    >
      {f.emAndamento ? <Loader2 className="h-3 w-3 animate-spin" /> : f.ok === false ? <AlertTriangle className="h-3 w-3" /> : <Check className="h-3 w-3" />}
      {rotulo}
      {f.resumo && !f.emAndamento && f.ok !== false && <span className="opacity-60">· {f.resumo}</span>}
    </span>
  );
}

/**
 * O que a pessoa está aprovando, em conteúdo — não em id. `campos` vem da
 * prévia da ferramenta (texto do template, mensagem, público da campanha) e
 * `revisarPath` abre a tela para conferir o resto antes de confirmar.
 */
function CartaoConfirmacao(props: {
  pendencia: AssistentePendencia;
  desabilitado: boolean;
  onRevisar: (a: AssistenteAcao) => void;
  onResposta: (aprovado: boolean) => void;
}) {
  const { pendencia, desabilitado, onRevisar, onResposta } = props;
  const linhas =
    pendencia.campos && pendencia.campos.length > 0
      ? pendencia.campos.map((c) => [c.rotulo, c.valor] as const)
      : Object.entries(pendencia.args)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => [k, legivel(v)] as const);
  return (
    <div className="rounded-xl border border-amber-300/60 bg-amber-50 p-3 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">
      <p className="flex items-center gap-1.5 font-medium">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        Confirmar: {pendencia.titulo}
      </p>
      {linhas.length > 0 && (
        <dl className="mt-2 space-y-1.5 text-xs">
          {linhas.map(([rotulo, valor]) => (
            <div key={rotulo} className="grid grid-cols-[6.5rem_1fr] gap-x-3">
              <dt className="text-muted-foreground">{rotulo}</dt>
              <dd className="min-w-0">
                <ValorLongo texto={valor} />
              </dd>
            </div>
          ))}
        </dl>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={desabilitado} onClick={() => onResposta(true)}>
          <Check className="mr-1.5 h-3.5 w-3.5" /> Confirmar
        </Button>
        {pendencia.revisarPath && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onRevisar({ tipo: "abrir", rotulo: "Revisar", path: pendencia.revisarPath! })}
          >
            <Eye className="mr-1.5 h-3.5 w-3.5" /> Revisar na tela
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={desabilitado} onClick={() => onResposta(false)}>
          Cancelar
        </Button>
      </div>
      {pendencia.revisarPath && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Revisar abre a tela sem perder esta confirmação — volte aqui para confirmar.
        </p>
      )}
    </div>
  );
}

/**
 * Anexo de imagem aparece como MINIATURA, não como nome de arquivo: quem
 * cola um print quer conferir que colou o print certo, e "captura-182747.png"
 * não responde isso. Documento e áudio seguem como pílula — ali o nome é a
 * informação útil.
 *
 * A miniatura usa a imagem local enquanto a mensagem está sendo escrita
 * (`previewUrl`, sem ida ao servidor) e o endpoint do anexo depois de
 * enviada, quando a página recarrega e o arquivo local já não existe.
 */
function ChipAnexo({ a, claro, onRemover }: { a: AnexoUi; claro?: boolean; onRemover?: () => void }) {
  const src = a.previewUrl ?? (a.kind === "image" ? `/api/assistente/anexo/${a.id}` : null);
  if (a.kind === "image" && src) {
    return (
      <span className="group/anexo relative inline-block">
        <img
          src={src}
          alt={a.fileName}
          title={a.fileName}
          loading="lazy"
          className={cn("h-20 w-20 rounded-xl border object-cover", claro ? "border-white/30" : "border-border")}
        />
        {onRemover && (
          <button
            type="button"
            onClick={onRemover}
            aria-label={`Remover ${a.fileName}`}
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-colors hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }
  const Icone = a.kind === "audio" ? Music : FileText;
  return (
    <span className={cn("inline-flex max-w-[240px] items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs", claro ? "border-white/30 bg-white/15" : "border-border bg-muted/50")}>
      <Icone className="h-3.5 w-3.5 shrink-0 opacity-70" />
      <span className="min-w-0 flex-1 truncate">{a.fileName}</span>
      <span className={cn("shrink-0 text-[10px]", claro ? "text-white/60" : "text-muted-foreground")}>{tamanhoLegivel(a.size)}</span>
      {onRemover && (
        <button type="button" onClick={onRemover} className="shrink-0 rounded-full hover:bg-black/10" aria-label={`Remover ${a.fileName}`}>
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

function tamanhoLegivel(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`.replace(".", ",");
}

function VazioComSugestoes({
  compacto,
  onEscolher,
  bloqueio,
  semPlano,
  modulo,
}: {
  compacto?: boolean;
  onEscolher: (t: string) => void;
  bloqueio: string | null;
  semPlano?: boolean;
  modulo?: ModuloAssistente;
}) {
  const sugestoes = modulo?.sugestoes ?? SUGESTOES_GERAIS;
  return (
    <div className={cn("mx-auto flex h-full flex-col items-center justify-center gap-5 text-center", compacto ? "max-w-full px-1" : "max-w-xl")}>
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[color:var(--brand-accent)]/15 text-[color:var(--brand-accent)]">
        {modulo ? <IconeModulo nome={modulo.icone} className="h-6 w-6" /> : <Sparkles className="h-6 w-6" />}
      </div>
      <div>
        <h2 className="text-lg font-semibold">{modulo ? modulo.nome : "O que você quer fazer?"}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {modulo
            ? modulo.resumo
            : "Peça para consultar, criar ou mudar qualquer coisa no sistema — ou pergunte como se faz. Use o + para escolher a área."}
        </p>
      </div>
      {semPlano ? (
        <CartaoUpgrade compacto={compacto} className="w-full" />
      ) : bloqueio ? (
        <p className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm dark:border-amber-500/30 dark:bg-amber-500/10">{bloqueio}</p>
      ) : (
        <div className={cn("grid w-full gap-2", compacto ? "grid-cols-1" : "grid-cols-2")}>
          {sugestoes.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onEscolher(t)}
              className="rounded-xl border border-border bg-card px-3 py-2 text-left text-sm transition-colors hover:bg-accent"
            >
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Composer ────────────────────────────────────────────────────────────────

function Composer(props: {
  compacto?: boolean;
  ocupado: boolean;
  desabilitado: boolean;
  microfone: boolean;
  /** Falso quando o plano não inclui o Assistente: o servidor recusaria o upload. */
  permiteAnexo: boolean;
  onEnviar: (texto: string, anexos: AnexoUi[]) => void;
  onCancelar: () => void;
  modulos: ModuloAssistente[];
  moduloAtivo?: ModuloAssistente;
  onEscolherModulo: (id: string | null) => void;
}) {
  const { compacto, ocupado, desabilitado, microfone, permiteAnexo, onEnviar, onCancelar, modulos, moduloAtivo, onEscolherModulo } = props;
  const [texto, setTexto] = useState("");
  const [anexos, setAnexos] = useState<AnexoUi[]>([]);
  const anexosRef = useRef<AnexoUi[]>([]);
  anexosRef.current = anexos;
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const anexar = trpc.assistente.anexar.useMutation();
  const transcrever = trpc.assistente.transcrever.useMutation();
  const gravador = useGravador({
    onTexto: (t) => setTexto((atual) => (atual ? `${atual} ${t}` : t)),
    transcrever: async (blob) => {
      const r = await transcrever.mutateAsync({ mimeType: blob.type || "audio/webm", dataBase64: await paraBase64(blob) });
      return r.texto;
    },
  });

  const enviar = () => {
    if (desabilitado || ocupado || enviandoAnexo) return;
    if (!texto.trim() && anexos.length === 0) return;
    onEnviar(texto, anexos);
    setTexto("");
    // A miniatura local já cumpriu o papel; daqui em diante a mensagem usa o
    // endpoint do anexo. Sem revogar, cada print colado fica na memória da
    // aba até ela fechar.
    for (const a of anexos) if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
    setAnexos([]);
    textareaRef.current?.focus();
  };

  const escolherArquivos = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    if (!permiteAnexo) {
      toast.info("Anexos fazem parte do Assistente, que ainda não está no seu plano.");
      return;
    }
    // O contador vem de uma ref, não do estado capturado: colar e arrastar ao
    // mesmo tempo (agora ambos possíveis) passavam do teto pelo fecho velho,
    // e o servidor descartava em silêncio o que a tela mostrava anexado.
    const lista = Array.from(files).slice(0, ASSISTENTE_LIMITES.anexosPorMensagem - anexosRef.current.length);
    if (lista.length === 0) {
      toast.warning(`No máximo ${ASSISTENTE_LIMITES.anexosPorMensagem} anexos por mensagem.`);
      return;
    }
    setEnviandoAnexo(true);
    try {
      for (const f of lista) {
        if (anexosRef.current.length >= ASSISTENTE_LIMITES.anexosPorMensagem) break;
        const teto = f.type.startsWith("image/") ? ASSISTENTE_LIMITES.imagemMaxBytes : f.type.startsWith("audio/") ? ASSISTENTE_LIMITES.audioMaxBytes : ASSISTENTE_LIMITES.anexoMaxBytes;
        if (f.size > teto) {
          toast.error(`${f.name}: acima de ${Math.round(teto / 1024 / 1024)} MB.`);
          continue;
        }
        try {
          const r = await anexar.mutateAsync({ fileName: nomeDoArquivo(f), mimeType: f.type || "application/octet-stream", dataBase64: await paraBase64(f) });
          // Miniatura sem ida ao servidor enquanto a mensagem não foi enviada.
          const previewUrl = r.kind === "image" ? URL.createObjectURL(f) : undefined;
          const novo = { id: r.id, fileName: r.fileName, kind: r.kind, size: r.size, previewUrl };
          anexosRef.current = [...anexosRef.current, novo];
          setAnexos((a) => [...a, novo]);
        } catch (err) {
          toast.error(toUserFriendlyError(err, `Não consegui anexar ${nomeDoArquivo(f)}`));
        }
      }
    } finally {
      setEnviandoAnexo(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "0px";
    el.style.height = `${Math.min(el.scrollHeight, compacto ? 140 : 200)}px`;
  }, [texto, compacto]);

  return (
    <div className={cn("shrink-0 border-t bg-background", compacto ? "p-2" : "px-4 py-3")}>
      <div className={cn("mx-auto", compacto ? "max-w-full" : "max-w-3xl")}>
        {moduloAtivo && (
          <div className="mb-2 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--brand-accent)]/40 bg-[color:var(--brand-accent)]/10 px-2.5 py-1 text-xs font-medium text-[color:var(--brand-accent)]">
              <IconeModulo nome={moduloAtivo.icone} className="h-3.5 w-3.5" />
              {moduloAtivo.nome}
              <button type="button" onClick={() => onEscolherModulo(null)} aria-label="Tirar o foco desta área" className="rounded-full hover:bg-black/10">
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}
        {anexos.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {anexos.map((a) => (
              <ChipAnexo
                key={a.id}
                a={a}
                onRemover={() => {
                  if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
                  setAnexos((lista) => lista.filter((x) => x.id !== a.id));
                }}
              />
            ))}
          </div>
        )}
        <div
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes("Files")) {
              e.preventDefault();
              setArrastando(true);
            }
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => {
            if (e.dataTransfer.files.length === 0) return;
            e.preventDefault();
            setArrastando(false);
            void escolherArquivos(Array.from(e.dataTransfer.files));
          }}
          className={cn(
            "flex items-end gap-1.5 rounded-2xl border bg-card p-1.5 shadow-sm focus-within:ring-1 focus-within:ring-ring",
            arrastando && "border-[color:var(--brand-accent)] ring-1 ring-[color:var(--brand-accent)]",
          )}
        >
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ASSISTENTE_MIMES_ACEITOS.join(",") + ",.md,.csv,.txt,.json"}
            className="hidden"
            onChange={(e) => void escolherArquivos(e.target.files)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 rounded-xl" disabled={desabilitado || enviandoAnexo} title="Anexar arquivo ou escolher a área do sistema">
                {enviandoAnexo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="max-h-[60vh] w-72 overflow-y-auto">
              <DropdownMenuItem onClick={() => fileRef.current?.click()}>
                <Paperclip className="mr-2 h-4 w-4" />
                <span>Anexar arquivo</span>
                <span className="ml-auto text-[10px] text-muted-foreground">PDF, imagem, áudio…</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] font-normal text-muted-foreground">
                Trabalhar em uma área do sistema
              </DropdownMenuLabel>
              {moduloAtivo && (
                <DropdownMenuItem onClick={() => onEscolherModulo(null)}>
                  <X className="mr-2 h-4 w-4" />
                  <span>Sem área definida</span>
                </DropdownMenuItem>
              )}
              {modulos.map((m) => (
                <DropdownMenuItem key={m.id} onClick={() => onEscolherModulo(m.id)} className="items-start gap-2">
                  <IconeModulo nome={m.icone} className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-tight">{m.nome}</p>
                    <p className="line-clamp-2 text-[11px] leading-snug text-muted-foreground">{m.resumo}</p>
                  </div>
                  {moduloAtivo?.id === m.id && <Check className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-accent)]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Textarea
            ref={textareaRef}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onPaste={(e) => {
              // Print de tela colado com Ctrl/Cmd+V é o jeito mais rápido de
              // mostrar um problema ao assistente. Sem isto o navegador só
              // colava o texto e a imagem se perdia.
              const arquivos = Array.from(e.clipboardData?.files ?? []);
              if (arquivos.length === 0) return;
              e.preventDefault();
              void escolherArquivos(arquivos);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                enviar();
              }
            }}
            placeholder={
              gravador.gravando
                ? `Ouvindo… ${gravador.segundos}s — clique no microfone para parar`
                : moduloAtivo
                  ? `Peça algo de ${moduloAtivo.nome} ou pergunte como fazer…`
                  : "Peça algo ou pergunte como fazer…"
            }
            rows={1}
            disabled={desabilitado}
            className="min-h-9 flex-1 resize-none border-0 bg-transparent px-2 py-2 text-sm shadow-none focus-visible:ring-0"
          />
          {microfone && (
            <Button
              type="button"
              variant={gravador.gravando ? "destructive" : "ghost"}
              size="icon"
              className={cn("h-9 w-9 shrink-0 rounded-xl", gravador.gravando && "animate-pulse")}
              disabled={desabilitado || gravador.transcrevendo}
              onClick={() => (gravador.gravando ? gravador.parar() : void gravador.iniciar())}
              title={gravador.gravando ? "Parar gravação" : "Falar"}
            >
              {gravador.transcrevendo ? <Loader2 className="h-4 w-4 animate-spin" /> : gravador.gravando ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
          )}
          {ocupado ? (
            <Button type="button" variant="secondary" size="icon" className="h-9 w-9 shrink-0 rounded-xl" onClick={onCancelar} title="Parar">
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button type="button" size="icon" className="h-9 w-9 shrink-0 rounded-xl" disabled={desabilitado || enviandoAnexo || (!texto.trim() && anexos.length === 0)} onClick={enviar} title="Enviar (Enter)">
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
        {!compacto && (
          <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
            Cole um print com Ctrl+V ou arraste um arquivo. No <strong className="font-medium">+</strong> você anexa e escolhe a área. O Assistente age com as suas permissões; ações que afetam clientes pedem confirmação.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Microfone ───────────────────────────────────────────────────────────────

/**
 * Microfone para ditar uma ideia inteira: o texto transcrito cai no campo
 * para o usuário revisar antes de enviar, e o contador mostra que ainda está
 * ouvindo (sem ele, uma gravação longa parece travada).
 */
function useGravador(opts: { onTexto: (t: string) => void; transcrever: (blob: Blob) => Promise<string> }) {
  const [gravando, setGravando] = useState(false);
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const partesRef = useRef<Blob[]>([]);

  useEffect(() => {
    if (!gravando) return;
    setSegundos(0);
    const t = setInterval(() => setSegundos((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [gravando]);

  const iniciar = async () => {
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Seu navegador não suporta gravação de áudio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported(m));
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      partesRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) partesRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setGravando(false);
        const blob = new Blob(partesRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1_000) return;
        setTranscrevendo(true);
        try {
          const t = await opts.transcrever(blob);
          if (t.trim()) opts.onTexto(t.trim());
          else toast.info("Não entendi nada no áudio.");
        } catch (err) {
          toast.error(toUserFriendlyError(err, "Não consegui transcrever"));
        } finally {
          setTranscrevendo(false);
        }
      };
      recRef.current = rec;
      rec.start();
      setGravando(true);
    } catch {
      toast.error("Sem acesso ao microfone. Libere a permissão no navegador.");
    }
  };

  const parar = () => {
    recRef.current?.stop();
    recRef.current = null;
  };

  useEffect(() => () => recRef.current?.stop(), []);

  return { gravando, transcrevendo, segundos, iniciar, parar };
}

/**
 * Print colado chega como File sem nome (ou "image.png"), e três "image.png"
 * na mesma conversa ficam indistinguíveis no histórico.
 */
function nomeDoArquivo(f: File): string {
  const generico = !f.name || /^image\.(png|jpe?g|webp)$/i.test(f.name);
  if (!generico) return f.name;
  const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
  const agora = new Date();
  const carimbo = `${agora.getHours().toString().padStart(2, "0")}${agora.getMinutes().toString().padStart(2, "0")}${agora.getSeconds().toString().padStart(2, "0")}`;
  return `captura-${carimbo}.${ext}`;
}

function paraBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
